import { useRef, useLayoutEffect } from 'react';
import Avatar from '../Avatar';
import { SECTION_COLOR, OK, prijmeniJmeno } from '../lide';

/**
 * Organizace → pohled Strom. Druhý pohled na tatáž data jako seznam (hledání
 * a pohledy nad ním platí i tady), ne vlastní podstránka.
 *
 * Proti původnímu stromu: bez hover tooltipu (na dotyku nikdy nezasvítí —
 * norma §1b), stav jen tečkou (aktivní × ostatní), vybraný člověk zvýrazněný.
 * Klik na uzel otevře kartu vedle — stejně jako v seznamu.
 */

export function sestavStrom(seznam) {
    const mapa = {};
    seznam.forEach(z => { mapa[z.id] = { ...z, deti: [] }; });
    const koreny = [];
    seznam.forEach(z => {
        if (z.nadrizeny_id && mapa[z.nadrizeny_id]) mapa[z.nadrizeny_id].deti.push(mapa[z.id]);
        else koreny.push(mapa[z.id]);
    });
    return koreny;
}

function Uzel({ uzel, onSelect, aktivniId, isDark }) {
    const vybrany = aktivniId === uzel.id;
    return (
        <button type="button" onClick={() => onSelect(uzel.id)}
            className="flex items-center text-left"
            style={{
                gap: 10, padding: '10px 12px', borderRadius: 10, minWidth: 190, maxWidth: 240, cursor: 'pointer',
                background: vybrany ? `${SECTION_COLOR}1f` : (isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-raised)'),
                border: `1px solid ${vybrany ? `${SECTION_COLOR}88` : (isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)')}`,
            }}>
            <Avatar z={uzel} size={36} />
            <span className="min-w-0 flex-1">
                <span className="flex items-center" style={{ gap: 6 }}>
                    <span className="truncate text-[13px] font-semibold" style={{ color: 'var(--text-primary)' }}>{prijmeniJmeno(uzel)}</span>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: uzel.stav === 'aktivni' ? OK : 'rgba(148,163,184,0.6)' }} />
                </span>
                {uzel.pozice?.nazev && <span className="block truncate text-[12px]" style={{ color: 'var(--text-secondary)' }}>{uzel.pozice.nazev}</span>}
            </span>
        </button>
    );
}

function Vetev({ uzel, onSelect, aktivniId, isDark }) {
    const detiRef = useRef(null);
    const carRef = useRef(null);
    const deti = uzel.deti || [];
    const vetve = deti.filter(d => d.deti.length > 0);
    const listy = deti.filter(d => d.deti.length === 0);
    // Víc než čtyři listy pod jedním člověkem = mřížka po pozicích, jinak by strom utekl do šířky.
    const kompaktne = listy.length > 4;
    const skupiny = kompaktne
        ? Object.entries(listy.reduce((acc, l) => { const k = l.pozice?.nazev || 'Ostatní'; (acc[k] = acc[k] || []).push(l); return acc; }, {}))
        : [];
    const slotu = kompaktne ? vetve.length + skupiny.length : deti.length;
    const cara = isDark ? 'rgba(255,255,255,0.15)' : 'var(--border-strong)';

    useLayoutEffect(() => {
        const box = detiRef.current;
        const bar = carRef.current;
        if (!box || !bar) return;
        if (slotu <= 1 || box.children.length < 2) { bar.style.display = 'none'; return; }
        const prvni = box.children[0];
        const posledni = box.children[box.children.length - 1];
        const l = prvni.offsetLeft + prvni.offsetWidth / 2;
        const r = posledni.offsetLeft + posledni.offsetWidth / 2;
        bar.style.display = 'block';
        bar.style.left = `${l}px`;
        bar.style.width = `${r - l}px`;
    }, [slotu]);

    const podUzlem = (obsah, klic) => (
        <div key={klic} className="flex flex-col items-center">
            <div style={{ width: 2, height: 20, background: cara }} />
            {obsah}
        </div>
    );

    return (
        <div className="flex flex-col items-center">
            <Uzel uzel={uzel} onSelect={onSelect} aktivniId={aktivniId} isDark={isDark} />
            {deti.length > 0 && (
                <div className="flex flex-col items-center w-full">
                    <div style={{ width: 2, height: 20, background: cara }} />
                    <div style={{ position: 'relative' }}>
                        <div ref={carRef} style={{ position: 'absolute', top: 0, height: 2, background: cara, display: 'none' }} />
                        <div ref={detiRef} className="flex items-start" style={{ gap: 14 }}>
                            {kompaktne ? (
                                <>
                                    {vetve.map(d => podUzlem(<Vetev uzel={d} onSelect={onSelect} aktivniId={aktivniId} isDark={isDark} />, d.id))}
                                    {skupiny.map(([pozice, clenove]) => podUzlem(
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                            {clenove.map(c => <Uzel key={c.id} uzel={c} onSelect={onSelect} aktivniId={aktivniId} isDark={isDark} />)}
                                        </div>,
                                        `g-${pozice}`,
                                    ))}
                                </>
                            ) : deti.map(d => podUzlem(<Vetev uzel={d} onSelect={onSelect} aktivniId={aktivniId} isDark={isDark} />, d.id))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function OrgStrom({ zamestnanci, onSelect, aktivniId, isDark, mutedColor }) {
    const koreny = sestavStrom(zamestnanci);
    if (!koreny.length) {
        return <p className="text-center text-xs" style={{ color: mutedColor, padding: '40px 16px' }}>Nikdo neodpovídá.</p>;
    }
    return (
        <div style={{ overflow: 'auto', flex: 1, minHeight: 0, padding: '24px 16px' }}>
            <div className="flex justify-center" style={{ gap: 40, minWidth: 'min-content' }}>
                {koreny.map(k => <Vetev key={k.id} uzel={k} onSelect={onSelect} aktivniId={aktivniId} isDark={isDark} />)}
            </div>
            {koreny.length === zamestnanci.length && zamestnanci.length > 1 && (
                <p className="text-center text-xs" style={{ color: mutedColor, marginTop: 20 }}>
                    Hierarchie není nastavená — nadřízeného nastavíte v kartě zaměstnance.
                </p>
            )}
        </div>
    );
}
